"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import ModalWrapper from "./ModalWrapper";
import { useModalStore } from "@/stores/useModalStore";
import { getFirmRating } from "@/utils/reviewUtils";
import CircularSegmentsCount from "@/ui/CircularSegmentWithCount";
import { IMAGE_BASE } from "@/lib/imageBase";
import { trackCta } from "@/lib/analytics/trackCta";
import { CTA_TYPES } from "@/lib/analytics/ctaRegistry";

const COPIED_CTA_MS = 1000;
const STAR_CLASS = "size-[21.5px]";

function getCountryLogoSrc(country) {
  if (country?.logo) {
    return `${IMAGE_BASE}/${country.logo}`;
  }
  return null;
}

function getCountryFlagcdnSrc(country) {
  if (country?.code) {
    return `https://flagcdn.com/w160/${country.code.toLowerCase()}.png`;
  }
  return null;
}

function ModalStarRating({ rating }) {
  const numericRating = Number(rating) || 0;
  const fullStars = Math.floor(numericRating);
  const hasHalfStar = numericRating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="flex items-center gap-[3.07px]" aria-hidden>
      {Array.from({ length: fullStars }).map((_, i) => (
        <FaStar key={`full-${i}`} className={`${STAR_CLASS} text-[#FBBF24]`} />
      ))}
      {hasHalfStar && (
        <FaStarHalfAlt className={`${STAR_CLASS} text-[#FBBF24]`} />
      )}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <FaRegStar key={`empty-${i}`} className={`${STAR_CLASS} text-white/20`} />
      ))}
    </div>
  );
}

export default function FirmOfferModal() {
  const { modals, modalContext, closeModal } = useModalStore();
  // Openers pass `{ firm, placement }`. The placement travels with it so the outbound
  // click below is attributed to the section the visitor actually copied from —
  // featured slider, home listing or the firm listing page.
  const firm = modalContext?.firm ?? null;
  const placement = modalContext?.placement ?? null;
  const isOpen = Boolean(modals.firmOffer && firm);

  const [showCopied, setShowCopied] = useState(true);
  const [flagcdnReady, setFlagcdnReady] = useState(false);

  const countryLogoSrc = firm ? getCountryLogoSrc(firm.country) : null;
  const countryFlagcdnSrc = firm ? getCountryFlagcdnSrc(firm.country) : null;

  // Prefetch sharper flagcdn image; keep API logo visible until it arrives.
  useEffect(() => {
    setFlagcdnReady(false);

    if (!isOpen || !countryFlagcdnSrc) return;

    let settled = false;
    const markReady = () => {
      if (settled) return;
      settled = true;
      setFlagcdnReady(true);
    };

    const img = new window.Image();
    img.onload = markReady;
    img.onerror = () => {
      settled = true;
    };
    img.src = countryFlagcdnSrc;
    if (img.complete && img.naturalWidth > 0) markReady();

    return () => {
      settled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [isOpen, countryFlagcdnSrc, firm?.id]);

  // Prefer sharp flagcdn once ready; otherwise show cached API logo (or nothing).
  const displayFlagSrc =
    (flagcdnReady && countryFlagcdnSrc) || countryLogoSrc || null;

  // Always start on secondary "Copied" when the modal opens / firm changes.
  useEffect(() => {
    if (!isOpen) {
      setShowCopied(true);
      return;
    }

    setShowCopied(true);
    const timer = setTimeout(() => setShowCopied(false), COPIED_CTA_MS);
    return () => clearTimeout(timer);
  }, [isOpen, firm?.id, firm?.slug]);

  const rating = firm ? getFirmRating(firm) : "0.0";
  const years = Math.max(0, Number(firm?.yearsInOperation) || 0);

  const handleUseOffer = () => {
    if (!firm) return;

    // `promo_modal_visit_site` is the registry entry created for exactly this button:
    // the follow-up link in the modal shown after a code is copied. Tracked before
    // window.open — a new tab can take focus and suspend this page's JavaScript.
    trackCta({
      ctaType: CTA_TYPES.PROMO_MODAL_VISIT_SITE,
      placement,
      firmSlug: firm.slug,
      couponCode: firm.promoCode,
      targetUrl: firm.firmUrl,
    });

    if (firm.firmUrl) {
      window.open(firm.firmUrl, "_blank", "noopener,noreferrer");
    }
    if (firm.promoCode && navigator.clipboard) {
      navigator.clipboard.writeText(firm.promoCode).catch(() => {});
    }
  };

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={() => closeModal("firmOffer")}
      overlayClassName="bg-black/55"
      panelClassName="max-w-[621px] !rounded-[18.64px] !px-[24.57px] !pt-[39px] !pb-[27.96px]"
      panelStyle={{
        border: "none",
        borderRadius: "18.64px",
        background:
          "linear-gradient(105.07deg, rgba(217, 217, 217, 0.12) -2.98%, rgba(115, 115, 115, 0.06) 97.56%)",
        backdropFilter: "blur(9.15px)",
        WebkitBackdropFilter: "blur(9.15px)",
      }}
    >
      {firm && (
        <div className="flex w-full flex-col items-center gap-[41px]">
          {/* Top content — identity + meta */}
          <div className="flex w-full max-w-[552px] flex-col items-start gap-6 sm:flex-row sm:items-start sm:justify-between sm:gap-[72px]">
            {/* Left: logo, name, rating, promo */}
            <div className="flex w-full flex-col items-start gap-[21px] sm:max-w-[318px]">
              <div className="flex w-full items-center gap-[24.57px]">
                <div className="relative size-[86px] shrink-0 overflow-hidden rounded-[16.89px]">
                  <Image
                    src={`${IMAGE_BASE}/${firm.logoUrl}`}
                    alt={firm.name}
                    fill
                    className="object-contain"
                    sizes="86px"
                  />
                </div>
                <div className="flex min-w-0 flex-col items-start">
                  <h3 className="truncate text-[clamp(22px,4vw,30.71px)] font-bold leading-[43px] text-white">
                    {firm.name}
                  </h3>
                  <div className="mt-[6.14px] flex items-center gap-[12.29px]">
                    <ModalStarRating rating={rating} />
                    <span className="text-[21.5px] font-semibold leading-[31px] text-[#94A3B8]">
                      {rating}
                    </span>
                  </div>
                </div>
              </div>

              {/* Promo ticket — wide enough for ≥12-char codes */}
              <div className="flex h-[46px] w-full max-w-[280px] min-w-[171px] items-stretch overflow-hidden rounded-md border border-dashed border-[#6ED828] bg-black">
                <div className="flex w-[62px] shrink-0 items-center justify-center px-2 text-[#6ED828]">
                  <div className="flex items-start leading-none">
                    <span className="text-[19.56px] font-bold leading-[25px]">
                      {firm.promoDiscountPercentage ?? 0}%
                    </span>
                    <span className="ml-0.5 mt-[4.5px] text-[6.7px] font-medium uppercase leading-[8px]">
                      OFF
                    </span>
                  </div>
                </div>
                <div className="w-0 shrink-0 border-l border-dashed border-[#6ED828]" />
                <div className="flex min-w-[12ch] flex-1 items-center justify-center px-3">
                  <span className="whitespace-nowrap text-base font-semibold leading-5 text-white">
                    {firm.promoCode}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: country + years — fixed 148px column, centered stacks per Figma */}
            <div className="flex w-full shrink-0 flex-row items-start justify-center gap-8 sm:w-[148px] sm:flex-col sm:items-center sm:gap-[23.73px]">
              <div className="flex w-[148px] flex-col items-center gap-[7.42px]">
                <span className="whitespace-nowrap text-center text-[17.8px] font-normal leading-[18px] text-[#C1BDBE]">
                  Country of origin
                </span>
                {displayFlagSrc && (
                  <div className="relative h-[32.63px] w-[65.25px] overflow-hidden rounded-[4.45px] bg-white/10">
                    <Image
                      key={displayFlagSrc}
                      src={displayFlagSrc}
                      alt={firm.country?.name || "Country"}
                      fill
                      sizes="130px"
                      unoptimized={displayFlagSrc === countryFlagcdnSrc}
                      className="object-cover"
                    />
                  </div>
                )}
              </div>
              <div className="flex w-[148px] flex-col items-center gap-[7.42px]">
                <span className="whitespace-nowrap text-center text-[17.8px] font-normal leading-[18px] text-[#C1BDBE]">
                  Years in operation:
                </span>
                <CircularSegmentsCount
                  filled={years}
                  total={10}
                  offset={2}
                  size={36}
                  radius={14}
                  strokeWidth={4}
                />
              </div>
            </div>
          </div>

          {/* CTA: secondary "Copied" first, then primary "Use Offer" */}
          {showCopied ? (
            <button
              type="button"
              disabled
              aria-live="polite"
              className="flex h-[60px] w-full max-w-[552px] cursor-default items-center justify-center rounded-full border border-[#6ED828] bg-transparent px-8 text-base font-semibold uppercase leading-6 tracking-[0.15px] text-[#6ED828]"
            >
              Copied
            </button>
          ) : (
            <button
              type="button"
              onClick={handleUseOffer}
              className="flex h-[60px] w-full max-w-[552px] items-center justify-center rounded-full bg-[#6ED828] px-8 text-base font-semibold uppercase leading-6 tracking-[0.15px] text-[#0F0F0F] transition hover:opacity-90"
            >
              Use Offer
            </button>
          )}
        </div>
      )}
    </ModalWrapper>
  );
}
